import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import CanvasHeader from './CanvasHeader';
import BrainstormMode from './BrainstormMode';
import OrganizeMode, { clampToCanvas } from './OrganizeMode';
import DeliverableView from '../ListView/DeliverableView';
import CompletionOverlay from '../Overlays/CompletionOverlay';
import RemovedConceptsOverlay from '../Overlays/RemovedConceptsOverlay';
import {
  getMap,
  updateMap,
  createMap,
  createTile,
  updateTile as updateTileApi,
  deleteTile as deleteTileApi,
  saveVersion,
  getVersions,
  restoreVersion,
  getShareToken,
  getBillingStatus,
} from '../../api/client';

export default function Canvas() {
  const { id: mapId } = useParams();
  const navigate = useNavigate();

  // Expired trials get the read-only viewer, never the editable canvas
  // (guards direct URLs and old bookmarks)
  useEffect(() => {
    getBillingStatus()
      .then((b) => {
        if (b.access === 'expired') navigate(`/map/${mapId}/view`, { replace: true });
      })
      .catch(() => {});
  }, [mapId, navigate]);

  const [map, setMap] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('brainstorm');
  const [versionCount, setVersionCount] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showCutPrompt, setShowCutPrompt] = useState(false);
  const [mapIsSet, setMapIsSet] = useState(false);
  const [versions, setVersions] = useState([]);
  const [buildScale, setBuildScale] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);

  const loadMap = useCallback(async () => {
    try {
      const data = await getMap(mapId);
      setMap(data);

      // Heal tiles stored outside the canvas (older builds allowed off-canvas
      // drops; those tiles render cut off or hidden in the map views). Fix
      // locally and persist best-effort so every view shows the same position.
      const healed = (data.tiles || []).map((t) => {
        if (t.zone !== 'HOLDING_PEN' && t.zone !== 'PROOF_1' && t.zone !== 'PROOF_2') return t;
        if (!t.posX && !t.posY) return t; // no saved position: views use seeded scatter
        const { x, y } = clampToCanvas(t.posX || 0, t.posY || 0);
        if (x === (t.posX || 0) && y === (t.posY || 0)) return t;
        updateTileApi(mapId, t.id, { posX: x, posY: y }).catch(() => {});
        return { ...t, posX: x, posY: y };
      });
      setTiles(healed);

      const hasOrganized = (data.tiles || []).some(
        (t) => t.zone !== 'HOLDING_PEN' && t.zone !== 'CUT'
      );

      const versionsList = await getVersions(mapId);
      const vList = Array.isArray(versionsList) ? versionsList : [];
      setVersions(vList);
      setVersionCount(vList.length);

      // If map has been set (has versions AND organized content), show Map view
      if (vList.length >= 1 && hasOrganized) {
        setMapIsSet(true);
        setViewMode('list');
      } else if (hasOrganized) {
        setViewMode('organize');
      }
    } catch (err) {
      console.error('Failed to load map:', err);
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => { loadMap(); }, [loadMap]);

  // ---- Tile helpers ----

  function getTilesByZone(zone) {
    return tiles.filter((t) => t.zone === zone).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // ---- Map updates ----

  async function handleUpdateMapName(name) {
    try {
      await updateMap(mapId, { name });
      setMap((m) => ({ ...m, name }));
    } catch (err) {
      console.error('Failed to update map name:', err);
    }
  }

  async function handleUpdateMeasurement(measurement) {
    try {
      await updateMap(mapId, { measurement });
      setMap((m) => ({ ...m, measurement }));
    } catch (err) {
      console.error('Failed to update measurement:', err);
    }
  }

  async function handleSaveVersion() {
    setSaving(true);
    try {
      await saveVersion(mapId);
      const vList = await getVersions(mapId);
      setVersions(Array.isArray(vList) ? vList : []);
      setVersionCount(Array.isArray(vList) ? vList.length : 0);
    } catch (err) {
      console.error('Failed to save version:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleRestoreVersion(versionId) {
    try {
      await restoreVersion(mapId, versionId);
      await loadMap();
    } catch (err) {
      console.error('Failed to restore version:', err);
    }
  }

  async function handleShare() {
    try {
      const { shareToken } = await getShareToken(mapId);
      const url = `${window.location.origin}/share/${shareToken}`;
      setShareUrl(url);
      navigator.clipboard.writeText(url);
    } catch (err) {
      console.error('Failed to generate share link:', err);
    }
  }

  // ---- Tile CRUD ----

  async function handleAddTile(text, posX, posY) {
    try {
      const newTile = await createTile(mapId, {
        text,
        zone: 'HOLDING_PEN',
        sortOrder: getTilesByZone('HOLDING_PEN').length,
        posX: posX || 0,
        posY: posY || 0,
      });
      setTiles((prev) => [...prev, newTile]);
    } catch (err) {
      console.error('Failed to create tile:', err);
    }
  }

  async function handleUpdateTile(tileId, data) {
    setTiles((prev) => prev.map((t) => (t.id === tileId ? { ...t, ...data } : t)));
    try {
      await updateTileApi(mapId, tileId, data);
    } catch (err) {
      console.error('Failed to update tile:', err);
      loadMap();
    }
  }

  async function handleCutTile(tileId) {
    handleUpdateTile(tileId, { zone: 'CUT', sortOrder: 0, posX: 0, posY: 0 });
  }

  async function handleRestoreTile(tileId) {
    const rx = 100 + Math.random() * 800;
    const ry = 300 + Math.random() * 400;
    handleUpdateTile(tileId, {
      zone: 'HOLDING_PEN',
      sortOrder: getTilesByZone('HOLDING_PEN').length,
      posX: rx,
      posY: ry,
    });
  }

  async function handleDeleteTile(tileId) {
    setTiles((prev) => prev.filter((t) => t.id !== tileId));
    try {
      await deleteTileApi(mapId, tileId);
    } catch (err) {
      console.error('Failed to delete tile:', err);
      loadMap();
    }
  }

  // ---- Render ----

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-txt-secondary">Loading map...</div>
      </div>
    );
  }

  if (!map) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-txt-secondary">Map not found</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-canvas flex flex-col overflow-hidden">
      <Navbar />
      <CanvasHeader
        mapName={map.name}
        clientName={map.client?.name || 'Unknown'}
        versionCount={versionCount}
        versions={versions}
        onUpdateName={handleUpdateMapName}
        onSave={handleSaveVersion}
        onRestoreVersion={handleRestoreVersion}
        saving={saving}
        viewMode={viewMode}
        onSetViewMode={setViewMode}
        tabsDisabled={viewMode === 'organize' && !mapIsSet}
        onShare={handleShare}
        shareUrl={shareUrl}
        measurement={map.measurement}
        onUpdateMeasurement={handleUpdateMeasurement}
      />

      {/* Keep all 3 views mounted and toggle with display so each preserves its state
          (scale/translate/zoom/etc.) across view switches. */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ display: viewMode === 'brainstorm' ? 'flex' : 'none' }}>
        <BrainstormMode
          tiles={getTilesByZone('HOLDING_PEN')}
          cutTiles={tiles.filter((t) => t.zone === 'CUT')}
          onAddTile={handleAddTile}
          onUpdateTile={handleUpdateTile}
          onCutTile={handleCutTile}
          onRestoreTile={handleRestoreTile}
          onDeleteTile={handleDeleteTile}
          onTransition={() => setViewMode('organize')}
        />
      </div>

      <div className="flex-1 overflow-hidden bg-white flex flex-col" style={{ display: viewMode === 'list' ? 'flex' : 'none' }}>
        <DeliverableView
          mapName={map.name}
          measurement={map.measurement}
          tiles={tiles}
          clientName={map.client?.name}
          updatedAt={map.updatedAt}
          onSwitchToBuild={() => setViewMode('organize')}
          mapId={mapId}
          buildScale={buildScale}
        />
      </div>

      {showCompletion && (
        <CompletionOverlay
          onDismiss={() => {
            setShowCompletion(false);
            const cutTiles = tiles.filter((t) => t.zone === 'CUT');
            if (cutTiles.length > 0) {
              setShowCutPrompt(true);
            } else {
              setViewMode('list');
            }
          }}
        />
      )}

      {showCutPrompt && (
        <RemovedConceptsOverlay
          cutTiles={tiles.filter((t) => t.zone === 'CUT')}
          onDelete={async () => {
            const cutTiles = tiles.filter((t) => t.zone === 'CUT');
            await Promise.all(cutTiles.map((t) => deleteTileApi(mapId, t.id)));
            setTiles((prev) => prev.filter((t) => t.zone !== 'CUT'));
            setShowCutPrompt(false);
            setViewMode('list');
          }}
          onSaveForLater={async () => {
            // Find or create a "Random Thoughts" map for this client
            const activeClient = JSON.parse(localStorage.getItem('activeClient') || '{}');
            const { getMaps } = await import('../../api/client');
            const allMaps = await getMaps(activeClient.id);
            let rtMap = allMaps.find((m) => m.name === 'Random Thoughts');
            if (!rtMap) {
              rtMap = await createMap({
                name: 'Random Thoughts',
                clientId: activeClient.id,
                measurement: 'A collection of concepts saved for future use',
              });
            }
            // Copy cut tiles — tag each with the source map name via linkUrl
            const cutTiles = tiles.filter((t) => t.zone === 'CUT');
            for (const t of cutTiles) {
              await createTile(rtMap.id, {
                text: t.text,
                zone: 'HOLDING_PEN',
                sortOrder: 0,
                posX: 240 + Math.random() * 720,
                posY: 140 + Math.random() * 420,
                linkUrl: map.name, // source map name stored here
              });
            }
            // Delete CUT tiles from this map so they don't re-appear on next completion
            await Promise.all(cutTiles.map((t) => deleteTileApi(mapId, t.id)));
            setTiles((prev) => prev.filter((t) => t.zone !== 'CUT'));
            setShowCutPrompt(false);
            setViewMode('list');
          }}
          onSkip={() => {
            setShowCutPrompt(false);
            setViewMode('list');
          }}
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden" style={{ display: viewMode === 'organize' ? 'flex' : 'none' }}>
        <OrganizeMode
          tiles={tiles}
          mapName={map.name}
          onUpdateTile={handleUpdateTile}
          onCutTile={handleCutTile}
          onRestoreTile={handleRestoreTile}
          onDeleteTile={handleDeleteTile}
          onAddTile={handleAddTile}
          onScaleChange={setBuildScale}
          onComplete={() => {
            handleSaveVersion();
            setMapIsSet(true);
            setShowCompletion(true);
          }}
        />
      </div>
    </div>
  );
}
